import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Heading } from "react-aria-components";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import type { Division, TournamentDivision } from "@/db/schema";
import { useTRPC } from "@/integrations/trpc/react";
import { invalidateAllTournamentQueries } from "@/lib/tournament-cache";

import { useHasPendingPoolMatches } from "../context";

export type CompletePoolsFormProps = {
  tournamentId: number;
  division: TournamentDivision & { division: Division };
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export function CompletePoolsForm({
  tournamentId,
  division,
  onOpenChange,
  ...props
}: CompletePoolsFormProps) {
  const hasPendingPoolMatches = useHasPendingPoolMatches();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, failureReason } = useMutation({
    ...trpc.tournaments.completePools.mutationOptions(),
    onSuccess: () => {
      invalidateAllTournamentQueries(queryClient, trpc);

      onOpenChange(false);
    },
  });

  const form = useAppForm({
    defaultValues: {
      // overwrite: false,
    },
    validators: {
      // onMount: schema,
      // onChange: schema,
    },
    onSubmit: () => {
      mutate({
        id: division.id,
      });
    },
  });

  return (
    <Modal {...props} onOpenChange={onOpenChange}>
      <div className="relative flex flex-col space-y-4 p-3">
        <Heading className={title({ size: "sm" })} slot="title">
          Verify Pools
        </Heading>

        <p className="mb-6 text-sm text-gray-700">
          Calculate finish positions for all pools in this division based on
          match results. If a playoff bracket exists, team assignments will be
          updated automatically. This will use the standard tie-breaking
          criteria:
        </p>

        <ul className="mb-6 list-inside list-disc space-y-1 text-sm text-gray-700">
          <li>Win-loss record</li>
          <li>Head-to-head results</li>
          <li>Point differential among tied teams</li>
          <li>Point differential among all teams</li>
        </ul>

        {hasPendingPoolMatches && (
          <p className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
            Some pool matches are still pending. All matches must be completed
            before pools can be finalized.
          </p>
        )}

        <form
          className="flex flex-col space-y-6"
          onSubmit={(e) => {
            e.preventDefault();

            form.handleSubmit();
          }}
        >
          {failureReason && (
            <form.AppForm>
              <form.Alert
                title="Unable to complete pools"
                description={failureReason.message}
              />
            </form.AppForm>
          )}

          <form.AppForm>
            <form.Footer>
              <Button onPress={() => onOpenChange(false)}>Cancel</Button>

              <form.SubmitButton
                requireChange={false}
                isDisabled={hasPendingPoolMatches}
              >
                Verify Pools
              </form.SubmitButton>
            </form.Footer>
          </form.AppForm>
        </form>
      </div>
    </Modal>
  );
}
